import React from 'react';
import { Col, Carousel } from 'react-bootstrap';
import { graphql, compose } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import SelectSubscription from '../select-subscription';
import Loading from '../loading';
import Environment from '../../services/Environment';
import BBAnalytics from '../../services/BBAnalytics';
import GraphQLTags from '../../services/GraphQLTags';
import PreCheckoutPopup from '../preCheckout-popup';
import BitsyModal from '../bitsy-modal';
import './style.css';

const CHARACTERS_REQUIRING_MARGIN = 4;

const getPlanAsClassName = (plan) => {
  let planClassName = `${plan.title}${plan.months}`;
  planClassName = planClassName.replace(/\s+/g, '');

  return planClassName;
};

const getCarouselItemClass = (plan) => {
  return `carouselItem${getPlanAsClassName(plan)}`;
};

const renderError = (isTopLevelComponent, history) => {
  if (isTopLevelComponent) {
    history.push('/error');
    return <Loading />;
  }
  return null;
};

const renderNoPlans = () => {
  return null;
};

const renderPartnerPlans = () => {
  return (
    <Col className='sub-wrapper linkToBumpCol'>
      <div>
        Please visit{' '}
        <a
          href='https://bumpboxes.com/'
          target='_blank'
          className='linkToBump'
          rel='noopener noreferrer'
          aria-label='Bump Boxes'
        >
          Bump Boxes
        </a>{' '}
        to subscribe!
      </div>
    </Col>
  );
};

const getFilteredPlans = (plansForProvider, showMonthlyPlans, planFilter) => {
  let filteredPlans;
  if (showMonthlyPlans) {
    filteredPlans = plansForProvider.filter(
      (plan) => plan.months === 'INFINITY' && plan.type === 'BOX',
    );
  } else {
    filteredPlans = plansForProvider.filter(
      (plan) => plan.months !== 'INFINITY' && plan.type === 'BOX',
    );
  }

  if (planFilter) {
    const splitPlans = planFilter.split(',');

    const newPlans = [];

    splitPlans.forEach((splitPlan) => {
      const monthsNumber = splitPlan.replace('b', '');
      const isBundled = splitPlan.endsWith('b');

      let foundMatchingPlan;
      if (isBundled) {
        foundMatchingPlan = filteredPlans.find(
          (plan) => plan.months === monthsNumber,
        );
      } else if (monthsNumber === 'm2m') {
        foundMatchingPlan = filteredPlans.find(
          (plan) => plan.title === 'Month to Month',
        );
      } else {
        foundMatchingPlan = filteredPlans.find(
          (plan) => plan.minimumMonths === Number(monthsNumber),
        );
      }

      if (foundMatchingPlan) {
        newPlans.push(foundMatchingPlan);
      }
    });

    if (newPlans.length > 0) {
      filteredPlans = newPlans;
    }
  }

  return filteredPlans;
};

const getMaxBulletLength = (plansForProvider) => {
  // Determine if any of the boxes require more or less spacing because
  // the content is longer.
  const point1Lengths = plansForProvider.map((plan) => {
    let point1;
    if (plan.highlights && plan.highlights.length) {
      point1 = plan.highlights.find((highlight) => highlight.order === 1);
    } else {
      point1 = plan.bulletPoints.point1;
    }
    return point1.length;
  });
  return Math.max(...point1Lengths);
};

const getIndexOfDefaultPlan = (filteredPlans) => {
  const defaultIndex = filteredPlans.findIndex((elem) => {
    return elem.isDefault;
  });

  let activeIndex = 2;
  if (defaultIndex >= 0) {
    activeIndex = defaultIndex;
  }

  return activeIndex;
};

const sendLoadEvent = () => {
  // used to set an activation event for Google Optimize
  const event = 'optimize.activate.plansvisible';
  BBAnalytics.addActivationEvent(event);
};

const subscriptionCouponsFromURLWithLocalStorageHandler = (props) => {
  let subscriptionCouponsFromURL =
    props.getSubscriptionCouponsByCode?.getSubscriptionCouponsByCode;
  if (!subscriptionCouponsFromURL) {
    const couponsFromStorage = localStorage.getItem(
      'subscriptionCouponsFromURL',
    );
    const subscriptionCouponsFromURLSetAt = localStorage.getItem(
      'subscriptionCouponsFromURLSetAt',
    );
    if (couponsFromStorage && subscriptionCouponsFromURLSetAt) {
      const tenDays = 10 * 24 * 60 * 60 * 1000;
      const tenDaysAgo = new Date(Date.now() - tenDays);
      if (tenDaysAgo < new Date(Number(subscriptionCouponsFromURLSetAt))) {
        subscriptionCouponsFromURL = JSON.parse(couponsFromStorage);
      } else {
        localStorage.removeItem('subscriptionCouponsFromURL');
        localStorage.removeItem('subscriptionCouponsFromURLSetAt');
      }
    }
  } else if (subscriptionCouponsFromURL && subscriptionCouponsFromURL.length) {
    localStorage.setItem(
      'subscriptionCouponsFromURL',
      JSON.stringify(subscriptionCouponsFromURL),
    );
    localStorage.setItem('subscriptionCouponsFromURLSetAt', Date.now());
  }

  return subscriptionCouponsFromURL;
};

const renderModal = (props) => {
  if (props.modalType === 'PRE-CHECKOUT PROMO') {
    return (
      <BitsyModal
        isShowingModal
        id='preCheckoutPromoModal'
        onExit={props.goToCart}
        type='modalWithComponent'
        content='preCheckoutPromoPopUpPremiumUpgrade'
        modalTitle={props.modalTitle}
        modalComponent={
          <PreCheckoutPopup
            updateTitle={props.updateTitle}
            handleAddPreCheckoutPromo={props.handleAddPreCheckoutPromo}
            exitModal={props.goToCart}
            isRecurringAddOnPromo
            id='subPreCheckoutPromo'
          />
        }
      />
    );
  }

  return null;
};

const renderPlansMobile = (
  props,
  filteredPlans,
  activeIndex,
  maxBullet1,
  subscriptionCouponsFromURL,
) => {
  return (
    <Col
      className='sub-wrapper sub-wrapper-carousel'
      id={props.subBoxContainerId ? props.subBoxContainerId : 'sub-wrapper'}
    >
      {renderModal(props)}
      <Carousel defaultActiveIndex={activeIndex} interval={false}>
        {filteredPlans.map((plan, planIndex) => (
          <Carousel.Item
            key={plan.title}
            className={getCarouselItemClass(plan)}
          >
            <SelectSubscription
              referralCode={props.referralCode}
              key={plan._id}
              plan={plan}
              nextStep={props.nextStep}
              requiresExtraMargin={
                plan.bulletPoints.point1.length <
                maxBullet1 - CHARACTERS_REQUIRING_MARGIN
              }
              isTopLevelComponent={props.isTopLevelComponent}
              activeIndex={activeIndex}
              planIndex={planIndex}
              handleAddPreCheckoutPromo={props.handleAddPreCheckoutPromo}
              modalType={props.modalType}
              goToCart={props.goToCart}
              subscriptionCouponsFromURL={subscriptionCouponsFromURL}
            />
          </Carousel.Item>
        ))}
      </Carousel>
    </Col>
  );
};

const renderPlansDesktop = (
  props,
  filteredPlans,
  maxBullet1,
  subscriptionCouponsFromURL,
) => {
  return (
    <Col
      xs={12}
      sm={12}
      md={{ span: 10, offset: 1 }}
      lg={12}
      className='sub-wrapper'
      id={props.subBoxContainerId ? props.subBoxContainerId : 'sub-wrapper'}
    >
      {renderModal(props)}
      {filteredPlans.map((plan) => (
        <SelectSubscription
          referralCode={props.referralCode}
          key={plan._id}
          plan={plan}
          nextStep={props.nextStep}
          requiresExtraMargin={
            plan.bulletPoints.point1.length <
            maxBullet1 - CHARACTERS_REQUIRING_MARGIN
          }
          isTopLevelComponent={props.isTopLevelComponent}
          handleAddPreCheckoutPromo={props.handleAddPreCheckoutPromo}
          modalType={props.modalType}
          goToCart={props.goToCart}
          subscriptionCouponsFromURL={subscriptionCouponsFromURL}
        />
      ))}
    </Col>
  );
};

const SelectSubscriptionBoxesWithoutData = (props) => {
  const { plansForProvider, error } = props.plansForProviderQuery;
  if (error) {
    return renderError(props.isTopLevelComponent, props.history);
  }

  const subscriptionCouponsFromURL = subscriptionCouponsFromURLWithLocalStorageHandler(
    props,
  );

  if (!plansForProvider || plansForProvider.length === 0) {
    if (Environment.store === 'pnmbump' || Environment.store === 'wtebump') {
      return renderPartnerPlans();
    }
    return renderNoPlans();
  }

  const filteredPlans = getFilteredPlans(
    plansForProvider,
    props.showMonthlyPlans,
    props.planFilter,
  );
  const maxBullet1 = getMaxBulletLength(filteredPlans);
  const activeIndex = getIndexOfDefaultPlan(filteredPlans);

  sendLoadEvent();

  if (window.innerWidth < 786 || window.screen.width < 786) {
    return renderPlansMobile(
      props,
      filteredPlans,
      activeIndex,
      maxBullet1,
      subscriptionCouponsFromURL,
    );
  }
  return renderPlansDesktop(
    props,
    filteredPlans,
    maxBullet1,
    subscriptionCouponsFromURL,
  );
};

const SelectSubscriptionBoxesWithData = compose(
  graphql(GraphQLTags.queries.plansForProviderQuery, {
    name: 'plansForProviderQuery',
    options: () => ({
      variables: {
        provider: Environment.store,
      },
      notifyOnNetworkStatusChange: true,
    }),
  }),
  graphql(GraphQLTags.queries.getSubscriptionCouponsByCode, {
    name: 'getSubscriptionCouponsByCode',
    skip: (ownProps) => !ownProps.matchParams?.couponCode,
    options: (ownProps) => ({
      variables: {
        code: ownProps.matchParams?.couponCode || undefined,
      },
    }),
  }),
)(withRouter(SelectSubscriptionBoxesWithoutData));

export { SelectSubscriptionBoxesWithoutData };

export default SelectSubscriptionBoxesWithData;
